// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createStore,
	createHook,
	type Action,
	createContainer,
	createActionsHook,
} from '@atlassian/react-sweet-state';
import type { State, Key, Index } from './types.tsx';

const CLEAR_DELAY = 300;

export const INITIAL_ELEMENT_STATE: State = {
	issue: {
		index: undefined,
		isIndexLocked: false,
		timeoutId: undefined,
	},
	field: {
		index: undefined,
		isIndexLocked: false,
		timeoutId: undefined,
	},
};

const actions = {
	setIndex:
		(key: Key, index: Index | number): Action<State> =>
		({ setState, getState, dispatch }) => {
			setState({
				[key]: {
					...getState()[key],
					index,
				},
			});
			dispatch(actions.lockIndex(key));
		},
	clearIndex:
		(key: Key): Action<State> =>
		({ setState, getState, dispatch }) => {
			setState({
				[key]: {
					...getState()[key],
					index: undefined,
				},
			});
			dispatch(actions.unlockIndex(key));
		},
	clearIndexAfterDelay:
		(key: Key): Action<State> =>
		({ setState, getState, dispatch }) => {
			const timeoutId = setTimeout(() => {
				if (!getState()[key].isIndexLocked) {
					dispatch(actions.clearIndex(key));
					setState({
						[key]: {
							...getState()[key],
							timeoutId: undefined,
						},
					});
				}
			}, CLEAR_DELAY);
			setState({
				[key]: {
					...getState()[key],
					isIndexLocked: false,
					timeoutId,
				},
			});
		},
	lockIndex:
		(key: Key): Action<State> =>
		({ setState, getState }) => {
			const { timeoutId } = getState()[key];
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
			setState({
				[key]: {
					...getState()[key],
					isIndexLocked: true,
					timeoutId: undefined,
				},
			});
		},
	unlockIndex:
		(key: Key): Action<State> =>
		({ setState, getState }) => {
			setState({
				[key]: {
					...getState()[key],
					isIndexLocked: false,
				},
			});
		},
};

export const InlineCreateActiveIndexContainer = createContainer();

const Store = createStore({
	initialState: INITIAL_ELEMENT_STATE,
	actions,
	containedBy: InlineCreateActiveIndexContainer,
});

export const useInlineCreateActiveIndex = createHook(Store);
export const useInlineCreateActiveIndexActions = createActionsHook(Store);

const getInlineIssueCreate = (state: State) => state.issue;
const getInlineFieldCreate = (state: State) => state.field;

const isIssueIndexEqual = (a: Index | undefined, b: Index) =>
	a !== undefined && a[0] === b[0] && a[1] === b[1];

/**
 * Return `true` if the issue create action should be displayed for a given issue index.
 */
export const useIsInlineIssueCreateActive = createHook(Store, {
	selector: (state: State, index: Index) => isIssueIndexEqual(state.issue.index, index),
});

export const useInlineIssueCreateActiveIndex = createHook(Store, {
	selector: getInlineIssueCreate,
});

export const useInlineFieldCreateActiveIndex = createHook(Store, {
	selector: getInlineFieldCreate,
});
